import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

import './Chart.css';

import type { ReactElement } from 'react';
import type { Series } from './series';

interface ChartProps {
  series: Array<Series>
  userMinYear: number
};

const COLORS: string[] = [
  '#4E79A7',
  '#F28E2B',
  '#E15759',
  '#76B7B2',
  '#59A14F',
  '#EDC948',
  '#B07AA1',
  '#FF9DA7',
  '#9C755F',
  '#BAB0AC',
];

function Chart({series, userMinYear}: ChartProps): ReactElement | null {

  // Determine origin year
  let maxStartYear = 0;
  // let showStartYearWarning = false;
  series.forEach((serie) => {
    if (serie.minYear > maxStartYear) {
      maxStartYear = serie.minYear;
    }
  });
  if (maxStartYear > userMinYear) {
    // showStartYearWarning = true;
  } else {
    maxStartYear = userMinYear;
  }

  // Construct dataseries.
  const datasets = series.map((serie, idx) => {
    const firstYear = serie.monthly[String(maxStartYear)];
    if (!firstYear) {
      return null;
    }

    // Find the first valid cpi in the start year.
    let cpiAtOrigin = 1;
    for (let i = 0; i < 12; i++) {
      const cpi = firstYear[String(i)];
      if (cpi > 0 && Number.isFinite(cpi)) {
        cpiAtOrigin = cpi;
        break;
      }
    }

    // Convert dataseries to chart values.
    const dataseries: {x: Date, y: number}[] = [];
    Object.entries(serie.monthly).forEach(([year, months]) => {
      Object.entries(months).forEach(([month, cpi]) => {
        if (cpi < 0 || !Number.isFinite(cpi)) {
          return;
        }
        const intYear = parseInt(year, 10);
        const intMonth = parseInt(month, 10);
        const date = new Date(intYear, intMonth, 1);
        dataseries.push({
          x: date,
          y: (cpi - cpiAtOrigin) / cpiAtOrigin * 100.0,
        });
      });
    });
    return {
      label: serie.label,
      data: dataseries,
      borderColor: COLORS[idx % COLORS.length],
      backgroundColor: COLORS[idx % COLORS.length],
      pointHoverBackgroundColor: COLORS[idx % COLORS.length] + '11',
    };
  }).filter(dataset => !!dataset);

  const data = {
    datasets,
  };
  const options = {
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'nearest',
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 10,
      },
      line: {
        borderWidth: 2,
      },
    },
    scales: {
      x: {
        type: 'time',
        min: new Date(maxStartYear, 0, 1),
      },
      y: {
        title: {
          display: true,
          text: 'Total CPI Inflation',
        },
        ticks: {
          callback: (val: string) => val + '%',
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: `CPI Inflation since ${maxStartYear}`,
      },
      subtitle: {
        display: true,
        text: 'Source: U.S. Bureau of Labor Statistics',
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`;
          },
          title: (context: any) => {
            const date = context[0].raw.x;
            return date.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            });
          },
        },
      },
    },
  };
  return (
    <div className="chart-container">
      <Line type="line" data={data} options={options} />
    </div>
  );
}

export default Chart;
